import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Redirect, Route, Switch } from 'react-router-dom';
import SuperPaymentForm from '../../components/SuperPaymentForm';
import FormTextField from '../../components/FormTextField';
import { useAppSelector } from '../../app/hooks';
import { OrderingConstants, OrderingSelectors } from 'polygon-ordering';
import PaymentProvider from '../../components/PaymentProvider';
import { TyroProvider } from '../../hooks/TyroProvider';
import { RedcatPaymentProvider } from '../../shared/redcatpayments/RedcatPaymentProvider';
import GiftCardTopUpRedcatPay from '../../components/GiftCardTopUpRedcatPay';
import LoyaltyPaymentScreen from '../LoyaltyPaymentScreen';
import { Show } from '../../components/atoms/Show';
import StandardButton from '../../components/StandardButton';
import history from '../../history';
import {
  GIFT_CARD_TOP_UP_ROUTE,
  GIFT_CARD_TOP_UP_FIELDS_ROUTE,
  GIFT_CARD_TOP_UP_PAYMENT_ROUTE,
} from '../../constants/loyaltyRoutes';
import type { GiftCardLimits } from './utils';
import {
  fetchGiftCardLimits,
  formatCents,
  isAboveMinTopUp,
  isWithinMaxTopUp,
  effectiveMinTopUpCents,
  effectiveMaxTopUpCents,
} from './utils';

export { GIFT_CARD_TOP_UP_ROUTE };

const { getGatewayType, getCurrencySymbol } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES } = OrderingConstants;

interface GiftCardTopUpFormValues {
  MemberNo: string;
  VerifyPIN: string;
  Amount: string;
}

interface GiftCardTopUpFieldsProps {
  giftCardLimits: GiftCardLimits | undefined;
  onGiftCardLimitsChange: (limits: GiftCardLimits | undefined) => void;
}

const GiftCardTopUpFields = ({
  giftCardLimits,
  onGiftCardLimitsChange,
}: GiftCardTopUpFieldsProps) => {
  const { t } = useTranslation();
  const { values, handleChange, isValid, setFieldError, validateForm } =
    useFormikContext<GiftCardTopUpFormValues>();
  const [lookingUpCard, setLookingUpCard] = useState(false);

  const lookUpGiftCardLimits = async () => {
    if (!values.MemberNo || !values.VerifyPIN) return;

    setLookingUpCard(true);
    try {
      const limits = await fetchGiftCardLimits(values.MemberNo, values.VerifyPIN);
      onGiftCardLimitsChange(limits);
    } catch {
      onGiftCardLimitsChange(undefined);
      setFieldError('VerifyPIN', t('form.invalidGiftCard'));
    } finally {
      setLookingUpCard(false);
      validateForm();
    }
  };

  const fields = [
    { name: 'MemberNo', type: 'text', label: t('placeholder.giftCardNo'), required: true },
    { name: 'VerifyPIN', type: 'text', label: t('placeholder.verifyPin'), required: true },
    { name: 'Amount', type: 'number', label: t('placeholder.giftcardAmount'), required: true },
  ];

  return (
    <div>
      {fields.map(field => (
        <FormTextField
          key={field.name}
          type={field.type}
          name={field.name}
          label={field.label}
          required={field.required}
          onChange={handleChange}
          handleBlur={field.name === 'VerifyPIN' ? lookUpGiftCardLimits : undefined}
        />
      ))}
      <div style={styles.buttonContainer}>
        <StandardButton
          label={t('button.proceedToPayment')}
          disabled={!isValid || lookingUpCard || !giftCardLimits}
          onClick={() => history.push(GIFT_CARD_TOP_UP_PAYMENT_ROUTE)}
          themeKey="loyaltyButton"
          containerStyle={styles.button}
        />
      </div>
    </div>
  );
};

const GiftCardTopUp = () => {
  const { t } = useTranslation();
  const gatewayType = useAppSelector(getGatewayType);
  const currencySymbol = useAppSelector(getCurrencySymbol);
  const [giftCardLimits, setGiftCardLimits] = useState<GiftCardLimits | undefined>(undefined);

  const schemas = {
    MemberNo: Yup.string().required(t('form.required')),
    VerifyPIN: Yup.string().required(t('form.required')),
    Amount: Yup.number()
      .required(t('form.required'))
      .min(1)
      .test('is-above-min-topup', '', function (value) {
        if (!giftCardLimits) return true;
        if (value === undefined || value === null) return true;
        if (isAboveMinTopUp(value, giftCardLimits)) return true;
        return this.createError({
          message: t('minTopUpAmount', {
            amount: formatCents(effectiveMinTopUpCents(giftCardLimits), currencySymbol),
          }),
        });
      })
      .test('is-within-max-topup', '', function (value) {
        if (!giftCardLimits) return true;
        if (value === undefined || value === null) return true;
        if (isWithinMaxTopUp(value, giftCardLimits)) return true;
        return this.createError({
          message: t('maxTopUpAmount', {
            amount: formatCents(effectiveMaxTopUpCents(giftCardLimits), currencySymbol),
          }),
        });
      }),
  };

  const validationSchema = Yup.object().shape(schemas);

  const isLegacyPaymentGateway = (
    [PAYMENT_GATEWAY_TYPES.STRIPE, PAYMENT_GATEWAY_TYPES.TYRO] as PaymentGatewayType[]
  ).includes(gatewayType);

  let LegacyProviderWrapper: any;
  if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
    LegacyProviderWrapper = PaymentProvider;
  } else if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
    LegacyProviderWrapper = TyroProvider;
  }

  const enableReCaptchaForGiftCard = useAppSelector(
    (state: any) => state.config.enableReCaptchaForGiftCard,
  );
  const reCaptchaSiteKey = useAppSelector((state: any) => state.config.reCaptchaSiteKey);

  if (!isLegacyPaymentGateway) {
    return (
      <div style={styles.container}>
        <RedcatPaymentProvider
          requireRecaptcha={enableReCaptchaForGiftCard}
          recaptchaSiteKey={reCaptchaSiteKey}
          purchaseType="giftcard_topup"
        >
          <Formik
            validationSchema={validationSchema}
            initialValues={{ MemberNo: '', VerifyPIN: '', Amount: '' }}
            onSubmit={() => {}}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Switch>
                  <Redirect
                    exact
                    from={GIFT_CARD_TOP_UP_ROUTE}
                    to={GIFT_CARD_TOP_UP_FIELDS_ROUTE}
                  />
                  <Route path={GIFT_CARD_TOP_UP_FIELDS_ROUTE}>
                    <GiftCardTopUpFields
                      giftCardLimits={giftCardLimits}
                      onGiftCardLimitsChange={setGiftCardLimits}
                    />
                  </Route>
                  <Route path={GIFT_CARD_TOP_UP_PAYMENT_ROUTE}>
                    <LoyaltyPaymentScreen>
                      <GiftCardTopUpRedcatPay />
                    </LoyaltyPaymentScreen>
                  </Route>
                </Switch>
              </Form>
            )}
          </Formik>
        </RedcatPaymentProvider>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Show when={isLegacyPaymentGateway}>
        <Formik
          validationSchema={validationSchema}
          initialValues={{ MemberNo: '', VerifyPIN: '', Amount: '' }}
          onSubmit={() => {}}
        >
          {({ handleSubmit, handleChange }) => (
            <Form onSubmit={handleSubmit}>
              <FormTextField
                type="text"
                name="MemberNo"
                label={t('placeholder.giftCardNo')}
                required
                onChange={handleChange}
              />
              <FormTextField
                type="text"
                name="VerifyPIN"
                label={t('placeholder.verifyPin')}
                required
                onChange={handleChange}
              />
              <FormTextField
                type="number"
                name="Amount"
                label={t('placeholder.giftcardAmount')}
                required
                onChange={handleChange}
              />
              <LegacyProviderWrapper>
                <SuperPaymentForm route="giftCardTopUp" />
              </LegacyProviderWrapper>
            </Form>
          )}
        </Formik>
      </Show>
    </div>
  );
};

const styles: Styles = {
  container: {
    textAlign: 'center',
    paddingTop: 8,
    paddingBottom: 8,
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
};

export default GiftCardTopUp;
